// 004 (FR-010–FR-018, FR-040–FR-045, FR-053, FR-054; US1; P-JOIN-5..7, P-ADD-1..5, BR-1, BR-14):
// Add student & delivery address, behind 003's /add-student?code=<CODE>&via=<LINK|QR|CODE>
// (specs/003-school-join-link/contracts/join-handoff.md). The school comes from the code: it is
// taken from 003's hand-off once, or looked up again after a reload, and is shown with its address.
// Change school swaps it for another school's typed code (source CODE; amendment 2026-10-08), so
// a student still only joins through a code (BR-14). Class and section come only from the chosen
// school's list. A parent without an address adds one with a map pin on the same screen;
// with addresses they choose one. Save sends the student (code + joinedVia, never a school id),
// then the address; a saved student clears the pending join and resets 003's students probe.
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { View } from "react-native";

import { isAppError } from "@/api/errors";
import type { Api } from "@/api/types";
import { JOIN_COPY } from "@/join/copy";
import { EnterSchoolCodeForm } from "@/join/EnterSchoolCodeForm";
import { handoff, type JoinHandoff } from "@/join/handoff";
import { useJoinClient } from "@/join/joinApi";
import { pendingJoin } from "@/join/pendingJoin";
import { isValidSchoolCode, normaliseSchoolCode } from "@/join/schoolCode";
import { SchoolSummaryCard } from "@/join/SchoolSummaryCard";
import { studentsProbe } from "@/join/studentsProbe";
import { useSchoolLookup } from "@/join/useSchoolLookup";
import { newIdempotencyKey } from "@/lib/idempotencyKey";
import type { PinValue } from "@/maps/PinPicker";
import { appNotices } from "@/shell/notice/store";
import { StateView } from "@/shell/StateView";
import { useNetworkStatus } from "@/shell/useNetworkStatus";
import { Button } from "@/ui/Button";
import { ConfirmDialog } from "@/ui/ConfirmDialog";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { AddressForm } from "../addresses/AddressForm";
import {
  type AddressFields,
  createAddressBody,
  emptyDraft,
} from "../addresses/addressBody";
import { ClassSectionPickers, type ClassSection } from "../ClassSectionPickers";
import { PROFILE_COPY, profileErrorMessage } from "../copy";
import { Card, OfflineNote, ProfileFrame, useProfileStyles } from "../ui";
import { useParentData } from "../useParentData";
import {
  type AddressErrors,
  validateAddress,
  validateName,
} from "../validation";
import { addressVM, canAddChild } from "../viewModels";
import { AddressChooser, NEW_ADDRESS } from "./AddressChooser";
import { createCombinedSave } from "./combinedSave";

const SOURCES: readonly Api["JoinSource"][] = ["LINK", "QR", "CODE"];

/** The school the student joins, with its address, and Change school (another school's code). */
function SchoolChoice({
  h,
  canChange,
  onPicked,
}: {
  h: JoinHandoff;
  canChange: boolean;
  onPicked: (code: string, school: Api["SchoolByCode"]) => void;
}) {
  const [changing, setChanging] = useState(false);
  if (changing) {
    return (
      <Card testID="add-student-change-school">
        <Text
          variant="heading"
          size="lg"
          accessibilityRole="header"
          aria-level={2}
        >
          {PROFILE_COPY.changeSchoolHeading}
        </Text>
        <EnterSchoolCodeForm
          showHeading={false}
          onConfirmed={(code, school) => {
            setChanging(false);
            onPicked(code, school);
          }}
          footer={
            <LinkButton onPress={() => setChanging(false)}>
              {PROFILE_COPY.keepSchool(h.school.name)}
            </LinkButton>
          }
        />
      </Card>
    );
  }
  return (
    <>
      <Text
        variant="heading"
        size="lg"
        accessibilityRole="header"
        aria-level={2}
      >
        {PROFILE_COPY.schoolSection}
      </Text>
      <SchoolSummaryCard school={h.school} testID="add-student-school">
        <Text tone="muted" testID="add-student-school-source">
          {PROFILE_COPY.schoolFrom(h.source)}
        </Text>
        {canChange ? (
          <>
            <Text tone="muted" size="sm">
              {PROFILE_COPY.addNote}
            </Text>
            <View>
              <Button
                variant="outline"
                onPress={() => setChanging(true)}
                testID="add-student-change-school-button"
              >
                {PROFILE_COPY.changeSchool}
              </Button>
            </View>
          </>
        ) : null}
      </SchoolSummaryCard>
    </>
  );
}

function AddForm({ h: initial }: { h: JoinHandoff }) {
  const styles = useProfileStyles();
  const router = useRouter();
  const join = useJoinClient();
  const { online } = useNetworkStatus();
  const { students, addresses, actions } = useParentData();
  const [h, setH] = useState(initial);
  const [name, setName] = useState("");
  const [placement, setPlacement] = useState<ClassSection>({
    classLevel: null,
    section: null,
  });
  const [draft, setDraft] = useState<AddressFields>(emptyDraft);
  const [pin, setPin] = useState<PinValue>({ placed: false, center: null });
  const [chosen, setChosen] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [askCancel, setAskCancel] = useState(false);
  const [fromPendingJoin, setFromPendingJoin] = useState(false);
  const [studentDone, setStudentDone] = useState(false);
  const [saver] = useState(() =>
    createCombinedSave({
      addStudent: (b, k) => actions.addStudent(b, k),
      addAddress: (b, k) => actions.addAddress(b, k),
      newKey: newIdempotencyKey,
      onStudentSaved: () => {
        setStudentDone(true);
        void pendingJoin.clear();
        studentsProbe.reset();
      },
    }),
  );

  // Know whether this screen came from a pending join (Cancel asks first, FR-017).
  useEffect(() => {
    let live = true;
    void pendingJoin.read().then((p) => {
      if (live) setFromPendingJoin(p?.code === initial.code);
    });
    return () => {
      live = false;
    };
  }, [initial.code]);

  // The children and addresses decide the limit and the address part.
  const missing = students.data === null || addresses.data === null;
  useEffect(() => {
    if (missing) void actions.refresh();
  }, [missing, actions]);

  if (missing) {
    return students.error && addresses.data === null ? (
      <StateView
        status="error"
        error={students.error}
        onRetry={() => void actions.refresh()}
      />
    ) : (
      <StateView status="loading" />
    );
  }

  const saved = addresses.data ?? [];
  const addressChoice =
    chosen ?? (saved.find((a) => a.isDefault) ?? saved[0])?.id ?? NEW_ADDRESS;
  const needsNewAddress = addressChoice === NEW_ADDRESS;

  async function leave() {
    await pendingJoin.clear();
    if (router.canGoBack()) router.back();
    else router.replace("/");
  }

  if (!canAddChild(students.data?.length ?? 0) && !studentDone) {
    return (
      <Card testID="add-student-limit">
        <Text>{PROFILE_COPY.childLimit}</Text>
        <Button variant="outline" onPress={() => void leave()}>
          {PROFILE_COPY.cancel}
        </Button>
      </Card>
    );
  }

  const noClasses = h.school.classes.length === 0;

  // Another school's code: its class list replaces the old one, so the class choice starts again.
  function pickSchool(code: string, school: Api["SchoolByCode"]) {
    if (code === h.code) return;
    setH({ code, source: "CODE", school });
    setPlacement({ classLevel: null, section: null });
    setErrors({});
    setFormError(null);
  }

  async function save() {
    if (pending || !online || noClasses) return;
    const nameError = validateName(name);
    const next = {
      name: nameError ?? undefined,
      classLevel: placement.classLevel ? undefined : PROFILE_COPY.chooseClass,
      section: placement.section ? undefined : PROFILE_COPY.chooseSection,
    };
    const aErrors = needsNewAddress
      ? validateAddress({ ...draft, pinPlaced: pin.placed })
      : {};
    setErrors(next);
    setAddressErrors(aErrors);
    setFormError(null);
    if (
      next.name ||
      next.classLevel ||
      next.section ||
      Object.keys(aErrors).length > 0
    )
      return;

    const studentBody: Api["StudentCreate"] = {
      name: name.trim(),
      schoolCode: h.code,
      classLevel: placement.classLevel as Api["ClassLevelCode"],
      section: placement.section!,
      joinedVia: h.source,
    };
    const addressBody =
      needsNewAddress && pin.center
        ? createAddressBody(draft, pin.center)
        : null;
    setPending(true);
    try {
      const result = await saver.submit(studentBody, addressBody);
      if (result.ok) {
        appNotices.post(PROFILE_COPY.added(result.student.name, h.school.name));
        router.replace("/profile");
        return;
      }
      const e = result.error;
      if (!isAppError(e)) throw e;
      if (result.part === "address") {
        const msg = profileErrorMessage(e, { action: "address" });
        setAddressErrors((msg.fields ?? {}) as AddressErrors);
        setFormError(
          msg.form
            ? `${PROFILE_COPY.partialFailure} ${msg.form}`
            : PROFILE_COPY.partialFailure,
        );
        return;
      }
      if (e.fieldErrors.some((f) => f.code === "NOT_IN_SCHOOL_CLASSES")) {
        // The school's list changed: look it up again, clear the choice and ask (FR-044).
        try {
          const school = await join.getSchoolByJoinCode(h.code);
          setH({ ...h, school });
        } catch {
          // keep the old list; the message still asks for a new choice
        }
        setPlacement({ classLevel: null, section: null });
        setErrors({ classLevel: PROFILE_COPY.classGone });
        return;
      }
      const msg = profileErrorMessage(e, {
        action: "add",
        child: name.trim(),
        school: h.school.name,
      });
      setErrors(msg.fields ?? {});
      setFormError(msg.form ?? null);
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <SchoolChoice h={h} canChange={!studentDone} onPicked={pickSchool} />
      <Card testID="add-student-student">
        <Text
          variant="heading"
          size="lg"
          accessibilityRole="header"
          aria-level={2}
        >
          {PROFILE_COPY.studentSection}
        </Text>
        <TextField
          label={PROFILE_COPY.name}
          value={name}
          onChangeText={setName}
          kind="name"
          error={errors.name}
          editable={!studentDone}
        />
        <ClassSectionPickers
          classes={h.school.classes}
          schoolName={h.school.name}
          value={placement}
          onChange={setPlacement}
          errors={{ classLevel: errors.classLevel, section: errors.section }}
          disabled={studentDone}
        />
      </Card>
      <Card testID="add-student-address">
        <Text
          variant="heading"
          size="lg"
          accessibilityRole="header"
          aria-level={2}
        >
          {PROFILE_COPY.addressSection}
        </Text>
        {saved.length > 0 ? (
          <AddressChooser
            items={saved.map(addressVM)}
            value={addressChoice}
            onChange={setChosen}
          />
        ) : null}
        {needsNewAddress ? (
          <AddressForm
            value={draft}
            onChange={setDraft}
            errors={addressErrors}
            onPin={setPin}
          />
        ) : null}
      </Card>
      {formError ? (
        <InlineMessage tone="error">{formError}</InlineMessage>
      ) : null}
      <OfflineNote online={online} />
      <View style={styles.buttons}>
        <Button
          variant="outline"
          onPress={() => (fromPendingJoin ? setAskCancel(true) : void leave())}
        >
          {PROFILE_COPY.cancel}
        </Button>
        <Button
          onPress={() => void save()}
          pending={pending}
          disabled={!online || noClasses || (needsNewAddress && !pin.placed)}
        >
          {needsNewAddress
            ? PROFILE_COPY.saveStudentAndAddress
            : PROFILE_COPY.saveStudent}
        </Button>
      </View>
      <ConfirmDialog
        visible={askCancel}
        title={PROFILE_COPY.stopAdding(h.school.name)}
        confirmLabel={PROFILE_COPY.stop}
        cancelLabel={PROFILE_COPY.keepGoing}
        onConfirm={() => {
          setAskCancel(false);
          void leave();
        }}
        onCancel={() => setAskCancel(false)}
      />
    </>
  );
}

export function AddStudentScreen() {
  const params = useLocalSearchParams<{ code?: string; via?: string }>();
  const code = normaliseSchoolCode(
    typeof params.code === "string" ? params.code : "",
  );
  const source: Api["JoinSource"] = SOURCES.includes(
    params.via as Api["JoinSource"],
  )
    ? (params.via as Api["JoinSource"])
    : "LINK";
  const [taken] = useState(() => handoff.take(code));
  const valid = isValidSchoolCode(code);
  const { state, retry } = useSchoolLookup(taken || !valid ? null : code);

  let body;
  if (taken) body = <AddForm h={taken} />;
  else if (!valid) body = <Text role="alert">{JOIN_COPY.invalid}</Text>;
  else if (state.status === "loading") body = <StateView status="loading" />;
  else if (state.kind === "active")
    body = <AddForm h={{ code, source, school: state.school }} />;
  else if (state.kind === "notActive")
    body = <Text role="alert">{JOIN_COPY.notActive(state.name)}</Text>;
  else if (state.kind === "invalid")
    body = <Text role="alert">{JOIN_COPY.invalid}</Text>;
  else if (state.kind === "rateLimited")
    body = <Text role="alert">{JOIN_COPY.rateLimited}</Text>;
  else body = <StateView status="error" error={state.error} onRetry={retry} />;

  return (
    <ProfileFrame testID="add-student" narrow>
      <Text
        variant="heading"
        size="x2l"
        accessibilityRole="header"
        aria-level={1}
      >
        {PROFILE_COPY.addTitle}
      </Text>
      <Text tone="muted">{PROFILE_COPY.addSubtitle}</Text>
      {body}
    </ProfileFrame>
  );
}
